/**
 * Focus rules for chrome controls (DESIGN.md §8.1, contract ButtonProps):
 *  - toolbar, tool-rail and dropdown buttons do not take focus on pointer down, so the editing region keeps it
 *    and Space/Enter keep reaching the global bindings after a click;
 *  - exception: when a text entry has focus, the pointer down is let through so the field blurs and commits its
 *    pending edit before the button acts (a dialog's "Apply" must see the typed value).
 */

import type { MouseEvent as ReactMouseEvent } from 'react';

/** True for elements that consume typing (text inputs, textareas, contenteditable). */
export function isTextEntry(el: Element | null): boolean {
  if (el === null) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) {
    return (
      !el.readOnly &&
      !['button', 'checkbox', 'radio', 'range', 'color', 'file', 'submit', 'reset', 'image'].includes(el.type)
    );
  }
  return el instanceof HTMLElement && el.isContentEditable;
}

/** onMouseDown handler for chrome buttons: keeps focus where it is (see module comment). */
export function keepFocusOnPointerDown(e: ReactMouseEvent): void {
  if (e.button !== 0) return;
  if (isTextEntry(document.activeElement)) return;
  e.preventDefault();
}

/** Every keyboard-focusable descendant, in DOM order (used for roving focus in toolbars). */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  const sel =
    'button, [href], input, select, textarea, [tabindex], [role="button"], [role="checkbox"], [role="radio"], [role="switch"], [role="combobox"]';
  return [...root.querySelectorAll<HTMLElement>(sel)].filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('tabindex') !== '-2' &&
      !el.closest('[hidden]') &&
      !el.closest('[inert]'),
  );
}
